import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import api from '../../utils/api';
import { getCourses, getSchoolByShortId } from '../../utils/directory';
import { LocalizationContext } from '../../hoc/Localization';
import { schoolVisited } from '../../utils/events/schools';
import { LocationContext } from '../../hoc/Location';
import { RouteContext } from '../../hoc/Route';
import { UserContext } from '../../hoc/User';
import { cleanString } from '../../utils';
import TitleContent from '../../components/TinyComponents/TitleContent';
import Header from '../../components/SchoolComponents/HeaderShow';
import Container from '../../components/Home/Container';
import useOnScreen from '../../components/useOnScreen';
import Button from '../../components/Button';
import styles from '../school/styles.less';
import Router from '../../routes';

const Loading = dynamic(() => import('../../components/Loading'));
const Error = dynamic(() => import('../_error'));
const SchoolInfo = dynamic(() => import('../../components/SchoolInfoShow'));
const ReviewForm = dynamic(() => import('../../components/Reviews/ReviewForm'));
const JsonLD = dynamic(() => import('../../components/JsonLD/School'));
const CardList = dynamic(() => import('../../components/CardList'));
const CardListHeader = dynamic(() => import('../../components/CardList/Header'));
const ReactPlayer = dynamic(() => import('../../components/VideoPlayer'));
const Api = dynamic(() => import('@tutorbot/directory-api'));
const Blog = dynamic(() => import('../../components/SchoolComponents/Blog'));
const Reviews = dynamic(() => import('../../components/Reviews'));
const Map = dynamic(() => import('../../components/CustomMap'));

const School = ({ data, load, schoolId }) => {
    const [school, setSchool] = React.useState(data);
    const [courses, setCourses] = React.useState([{}, {}, {}]);
    const [reviews, setReviews] = React.useState();
    const [loading, setLoading] = React.useState(load);
    const [videoLoading, setVideoLoading] = React.useState(true);
    const [blog, setBlog] = React.useState({});
    const [mapShow, setMapShow] = React.useState();
    const [desktop, setDesktop] = React.useState();
    const [fetched, setFetched] = React.useState();

    const {
        params: { id },
    } = React.useContext(RouteContext);
    const { t, locale } = React.useContext(LocalizationContext);
    const { locations } = React.useContext(LocationContext);
    const { user } = React.useContext(UserContext);

    const firstRef = React.useRef();
    const secondRef = React.useRef();

    const countries = locations
        .filter(location => location.default)
        .map(location => location.country);

    React.useEffect(() => {
        const windowWidth = window.screen.width;
        if (windowWidth > 768) {
            setDesktop(2);
        } else {
            setDesktop(1);
        }

        if (!load || (data && data.shortId === id)) {
            schoolVisited(data);
            return;
        }

        setLoading(true);

        if (data.shortId !== id) {
            setMapShow(true);
            setFetched(false);
            getSchoolByShortId(id, { countries }).then(res => {
                setSchool(res);
                setLoading(false);
                return schoolVisited(res);
            });
        }
    }, []);

    React.useEffect(() => {
        if (schoolId) {
            getSchoolByShortId(id, { countries }).then(res => {
                setLoading(false);
                setSchool(res);
                return schoolVisited(res);
            });
        }
    }, [schoolId]);

    const pushReview = review => {
        setReviews([...reviews, review]);
    };

    const firstShowMap = useOnScreen(firstRef, '250px');
    const secondShowMap = useOnScreen(secondRef, '350px');

    if (!mapShow && (firstShowMap || secondShowMap)) {
        setMapShow(true);
    }

    if (mapShow && !fetched) {
        (async () => {
            if (school) {
                if (school.shortId) {
                    setCourses(
                        (
                            await getCourses({
                                filters: { school: school.shortId, countries },
                                verified: 'all',
                                limit: 3,
                            })
                        ).data,
                    );
                    setFetched(true);
                }

                if (school.blogShortId) {
                    setBlog((await Api().blog.where('shortId', school.blogShortId).get()).data[0]);
                }
            }
        })();

        (async () => {
            if (school && school.external_id) {
                const res = await api.get(`user-ratings/reviews/${school.external_id}`);
                setReviews(res.reviews);
            }
        })();
    }

    if (loading) {
        return <Loading full />;
    }

    if (!school) {
        return <Error />;
    }

    const { title, description, location, cover_photo: coverPhoto, videoUrl } = school;

    school.logo = school.logo || '/static/images/school/defaultLogo.svg';

    const canEdit =
        school &&
        user &&
        ((user && user.isAdmin) ||
            (user &&
                user.schools &&
                !!user.schools.find(s => !!s && !!school && s.id === school.id)));

    const urlFront = coverPhoto ? coverPhoto.substring(0, coverPhoto.lastIndexOf('.')) : '';
    const ext = coverPhoto ? coverPhoto.slice(coverPhoto.lastIndexOf('.'), coverPhoto.length) : '';
    let smallUrl = '';
    if (desktop === 1 && urlFront && !coverPhoto.includes('static')) {
        smallUrl = urlFront.concat('-small').concat(ext);
    } else if (desktop > 1) {
        smallUrl = coverPhoto;
    } else {
        smallUrl = '';
    }
    return (
        <div className={styles.school}>
            <Head>
                <title>{`${title || t('school.create.title')} | ${t('meta.title.suffix')}`}</title>
                <meta name="description" content={cleanString(description)} />
                <meta name="Description" content={cleanString(description)} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={cleanString(school.about)} />
                <meta property="og:image" content={school.logo} />
                <meta property="og:image:width" content="600" />
                <meta property="og:image:height" content="600" />
                <meta property="og:locale" content={locale} />
            </Head>

            {canEdit && (
                <div className="editButtonsContainer">
                    <Button
                        size="default"
                        type="secondary"
                        className="editButton"
                        onClick={() => {
                            Router.pushRoute('createCourse', {
                                school: school.shortId,
                                locale,
                            });
                        }}
                    >
                        <img src="/static/images/school/plus1.svg" alt="add" />
                        <span>{t('details.edit.courses.add')}</span>
                    </Button>
                    <Button
                        size="default"
                        type="secondary"
                        data_gh="course_edit_button"
                        className="editButton editModeButton toolBarButton"
                        onClick={() => {
                            Router.pushRoute('school', {
                                school: school.link,
                                id: school.shortId,
                                locale,
                            });
                        }}
                    >
                        <img src="/static/images/school/edit.svg" alt="edit" />
                        <span>{t('course.edit.title')}</span>
                    </Button>
                </div>
            )}

            <div
                style={
                    coverPhoto && !coverPhoto.includes('googleapis')
                        ? {
                              backgroundImage: `url('${
                                  desktop && desktop > 1 ? coverPhoto : smallUrl
                              }')`,
                          }
                        : { backgroundImage: 'url(/static/images/defaultCoverPhoto5.jpg)' }
                }
                className={styles.cover}
            />

            <Container className={styles.headerContainer}>
                <Header school={school} canEdit={canEdit} />
            </Container>

            {!!videoUrl && (desktop > 1 || mapShow) && (
                <Container>
                    <div className={styles.playerContainer}>
                        {videoLoading ? <Loading className={styles.loading} /> : null}
                        <ReactPlayer
                            className={styles.player}
                            url={videoUrl}
                            onReady={() => {
                                setVideoLoading(false);
                            }}
                        />
                    </div>
                </Container>
            )}

            {!mapShow && <div style={{ height: '600px', width: '100%' }} />}
            <div ref={secondRef} style={{ height: '10px', width: '100%' }} />

            {(desktop > 1 || mapShow) && (
                <Container>
                    <SchoolInfo school={school} mapShow={mapShow} />
                </Container>
            )}

            {!!courses.length && mapShow && (
                <Container>
                    <TitleContent
                        title={
                            <CardListHeader
                                title={t('frontpage.search.courses')}
                                linkOptions={{
                                    route: 'search',
                                    queryParams: {
                                        school: school.shortId,
                                        verified: canEdit ? 'all' : true,
                                    },
                                }}
                            />
                        }
                        content={
                            <CardList data={courses} type="course" noButton reference="school" />
                        }
                    />
                </Container>
            )}

            {mapShow && (
                <Container>
                    <TitleContent
                        content={
                            <div className={styles.revewsContainer}>
                                {(user &&
                                    reviews &&
                                    !reviews.some(
                                        review => review.user && review.user.id === user.id,
                                    )) ||
                                !user ? (
                                    <ReviewForm
                                        externalId={school.external_id}
                                        pushReview={pushReview}
                                        id={school.id}
                                        type="school"
                                    />
                                ) : null}
                                {reviews && <Reviews loading={loading} reviews={reviews} />}
                            </div>
                        }
                    />
                </Container>
            )}

            {!mapShow && <div style={{ height: '600px', width: '100%' }} />}
            <div ref={firstRef} style={{ height: '10px', width: '100%' }} />

            {!!blog && !!blog.shortId && mapShow && (
                <Container>
                    <TitleContent
                        title={<CardListHeader title={t('school.blog.title')} link={false} />}
                        content={<>{!!blog && !!blog.shortId && <Blog blog={blog} />}</>}
                    />
                </Container>
            )}

            {!!location &&
                !!Object.keys(location).length &&
                !!location.lat &&
                !!location.lng &&
                mapShow && (
                    <Container>
                        <TitleContent
                            title={t('course.details.content.map')}
                            content={<Map location={location} school={school} isStaticMap />}
                        />
                    </Container>
                )}

            {mapShow && <JsonLD data={school} />}
        </div>
    );
};

School.propTypes = {
    load: PropTypes.bool,
    data: PropTypes.object,
    schoolId: PropTypes.string,
};

School.defaultProps = {
    data: {},
    load: false,
    schoolId: '',
};

School.getInitialProps = async (
    { req, res, query },
    router,
    { locationProps: { locations }, settingsProps: { domainSettings, domainsSettings } },
) => {
    if (process.browser) {
        return {
            schoolId: query.id,
            load: true,
        };
    }

    const data = await getSchoolByShortId(query.id, {
        countries: locations.filter(location => location.default).map(location => location.country),
    });

    if (!data) {
        res.statusCode = 404;
    }

    if (req && data) {
        const { domainCountries, defaultDomain, domain } = domainSettings;

        const {
            link,
            shortId,
            location: { country },
        } = data;

        let redirectDomain;

        if (domainCountries[domain] && !domainCountries[domain].includes(country)) {
            const domainsArray = Object.keys(domainCountries);
            for (let i = 0; i < domainsArray.length; i++) {
                if (domainCountries[domainsArray[i]].includes(country)) {
                    redirectDomain = domainsArray[i];
                    break;
                }
                if (!domain.includes('course.de')) {
                    redirectDomain = defaultDomain;
                }
            }
        }

        if (redirectDomain) {
            const { defaultLocale } = domainsSettings.find(d => d.domain === redirectDomain);
            res.writeHead(301, {
                Location: `https://${redirectDomain}/${defaultLocale}/schools/${link}/${shortId}`,
            });
            res.end();
        }
    }

    return {
        data,
    };
};

export default School;
